/**
 * SUPRIMENTOS > COMPRAS > ORDENS DE COMPRA (§8.4 / §10.5).
 *
 * A `LeftBar` prometia "Compras → Ordens" e a rota não existia. Esta é ela.
 *
 * O QUE ESTA TELA TEM DE DIZER, e é o critério do card: **por que a ordem está
 * em SUGERIR**. Não basta mostrar o selo — o dono precisa ler qual linha
 * estourou qual teto, quais linhas estão sem preço acordado vigente, e quanto
 * falta para o pedido mínimo do fornecedor (e para o frete grátis, quando há
 * regra). Um "SUGERIR" sem motivo transforma a trava numa reclamação sem
 * endereço.
 *
 * A DISTINÇÃO QUE A TELA NÃO PODE APAGAR: `envioSimuladoEm` preenchido com
 * `enviadaEm` nulo significa que o texto foi montado e **nada saiu**. Enquanto
 * o canal desta entrega for o de simulação, é sempre esse o caso — e a tela diz
 * isso com todas as letras, porque um dono que acha que o pedido saiu não liga
 * para o fornecedor.
 *
 * DINHEIRO PODE NÃO VIR (§4). Chave ausente vira travessão, nunca "R$ 0,00":
 * zero diria que o pedido não custa nada. A resposta traz `omitido` com a lista
 * do que ficou de fora, e o rodapé explica.
 */
import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import * as AlfredoComponents from "../../../components";
import * as AlfredoStyles from "../../../styles/global/global";
import * as Estilo from "../../../styles/components/compras/ComprasComum";
import { useDecodedToken } from "../../../services/jwt-decode.service";
import { useCombinedStore } from "../../../store";
import comprasService, {
  DetalheDaOrdem,
  Ordem,
  comoDataHora,
  comoQuantidade,
  comoReais,
  explicarModo,
} from "../../../services/compras.service";

/** Os estados da §8.4 que fazem sentido filtrar hoje. */
const ESTADOS = [
  "AGUARDANDO_APROVACAO",
  "APROVADA",
  "RECUSADA",
  "CANCELADA",
  "ENVIADA",
  "EXPIRADA",
];

function tomDoEstado(estado: string): "ok" | "atencao" | "info" | "erro" {
  if (estado === "APROVADA" || estado === "CONFIRMADA" || estado === "RECEBIDA") {
    return "ok";
  }
  if (estado === "RECUSADA" || estado === "CANCELADA" || estado === "EXPIRADA") {
    return "erro";
  }
  if (estado === "AGUARDANDO_APROVACAO") return "atencao";
  return "info";
}

const OrdersPage: React.FC = () => {
  const { authUserZustand } = useCombinedStore();
  const decryptedToken: any = useDecodedToken(authUserZustand?.Token);
  // `updatedBy` é obrigatório no corpo de todo POST: `request.userUuid` nunca é
  // populado no backend, e sem ele a gravação morre numa coluna NOT NULL.
  const quemAssina = decryptedToken?.userInfo?.userUuid ?? "";

  const [parametrosDaUrl, definirParametrosDaUrl] = useSearchParams();
  const estado = parametrosDaUrl.get("estado") ?? "";
  const selecionada = parametrosDaUrl.get("ordem") ?? "";

  const [ordens, definirOrdens] = useState<Ordem[]>([]);
  const [omitido, definirOmitido] = useState<string[]>([]);
  const [detalhe, definirDetalhe] = useState<DetalheDaOrdem | null>(null);
  const [carregando, definirCarregando] = useState(true);
  const [erro, definirErro] = useState<string | null>(null);
  const [aviso, definirAviso] = useState<string | null>(null);
  const [agindo, definirAgindo] = useState(false);

  const carregarLista = useCallback(async () => {
    definirCarregando(true);
    definirErro(null);
    try {
      const resposta = await comprasService.ordens({
        estado: estado || undefined,
        limit: 50,
      });
      definirOrdens(resposta.dados);
      definirOmitido(resposta.omitido ?? []);
    } catch (e: any) {
      definirErro(
        e?.response?.data?.message ?? "Não foi possível ler as ordens de compra.",
      );
    } finally {
      definirCarregando(false);
    }
  }, [estado]);

  useEffect(() => {
    carregarLista();
  }, [carregarLista]);

  useEffect(() => {
    if (!selecionada) {
      definirDetalhe(null);
      return;
    }
    let cancelado = false;
    comprasService
      .ordem(selecionada)
      .then((dados) => {
        if (!cancelado) definirDetalhe(dados);
      })
      .catch(() => {
        if (!cancelado) definirDetalhe(null);
      });
    return () => {
      cancelado = true;
    };
  }, [selecionada]);

  const abrir = (ordemUuid: string) => {
    const novos = new URLSearchParams(parametrosDaUrl);
    if (ordemUuid === selecionada) novos.delete("ordem");
    else novos.set("ordem", ordemUuid);
    definirParametrosDaUrl(novos);
  };

  const decidir = async (
    acao: "aprovar" | "recusar" | "cancelar",
    ordemUuid: string,
  ) => {
    if (!quemAssina) {
      definirAviso("Não foi possível identificar o usuário para assinar a decisão.");
      return;
    }
    const motivo =
      acao === "aprovar"
        ? ""
        : window.prompt(
            acao === "recusar"
              ? "Por que está recusando esta ordem?"
              : "Por que está cancelando esta ordem?",
          ) ?? "";
    definirAgindo(true);
    definirAviso(null);
    try {
      const resposta =
        acao === "aprovar"
          ? await comprasService.aprovar(ordemUuid, quemAssina)
          : acao === "recusar"
          ? await comprasService.recusar(ordemUuid, quemAssina, motivo)
          : await comprasService.cancelar(ordemUuid, quemAssina, motivo);
      definirAviso(resposta?.message ?? "Pronto.");
      await carregarLista();
      const atualizado = await comprasService.ordem(ordemUuid);
      definirDetalhe(atualizado);
    } catch (e: any) {
      // 409 aqui NÃO é erro do cliente: é a recusa explicada de cancelar uma
      // ordem que já saiu — e o texto dela é o que o dono precisa ler.
      definirAviso(
        e?.response?.data?.message ?? "Não foi possível registrar a decisão.",
      );
    } finally {
      definirAgindo(false);
    }
  };

  const ordem = detalhe?.ordem;
  const motivos = explicarModo(ordem?.motivoDoModo ?? null);

  return (
    <AlfredoStyles.Container>
      <AlfredoComponents.Breadcrumb />
      <AlfredoStyles.FlexContainer>
        <AlfredoStyles.FlexItem>
          <Estilo.Cabecalho>
            <div>
              <Estilo.Titulo>Ordens de compra</Estilo.Titulo>
              <Estilo.Subtitulo>
                Uma ordem por fornecedor e por loja, com o preço acordado vigente,
                o subtotal de cada linha e o total. Nada sai para o fornecedor
                antes de você aprovar.
              </Estilo.Subtitulo>
            </div>
          </Estilo.Cabecalho>

          <Estilo.Bloco>
            <Estilo.Icone>info</Estilo.Icone>
            <Estilo.Texto>
              O canal de mensagem está em <Estilo.Forte>modo simulação</Estilo.Forte>:
              o texto do pedido é montado e gravado no log, e{" "}
              <Estilo.Forte>nada sai para fornecedor real</Estilo.Forte>. Uma ordem
              com “envio simulado” não foi enviada — por isso ela continua em
              APROVADA e não vira ENVIADA.
            </Estilo.Texto>
          </Estilo.Bloco>

          <Estilo.Filtros>
            <Estilo.Campo>
              Estado
              <Estilo.Selecao
                value={estado}
                onChange={(e) => {
                  const novos = new URLSearchParams(parametrosDaUrl);
                  if (e.target.value) novos.set("estado", e.target.value);
                  else novos.delete("estado");
                  novos.delete("ordem");
                  definirParametrosDaUrl(novos);
                }}
              >
                <option value="">todos</option>
                {ESTADOS.map((valor) => (
                  <option key={valor} value={valor}>
                    {valor.replace(/_/g, " ").toLowerCase()}
                  </option>
                ))}
              </Estilo.Selecao>
            </Estilo.Campo>
            <Estilo.BotaoSecundario type="button" onClick={carregarLista}>
              Atualizar
            </Estilo.BotaoSecundario>
          </Estilo.Filtros>

          {aviso && (
            <Estilo.Painel tom="info">
              <Estilo.Texto>{aviso}</Estilo.Texto>
            </Estilo.Painel>
          )}
          {erro && (
            <Estilo.Painel tom="erro">
              <Estilo.Texto>{erro}</Estilo.Texto>
            </Estilo.Painel>
          )}

          {carregando && <Estilo.Vazio>Lendo as ordens…</Estilo.Vazio>}

          {!carregando && ordens.length === 0 && (
            <Estilo.Vazio>
              Nenhuma ordem de compra ainda.
              <br />
              As ordens nascem da regra da §10.5: o saldo de um insumo cruza o
              ponto de reposição, isso vira uma <strong>necessidade</strong>, e as
              necessidades do mesmo fornecedor e da mesma loja viram{" "}
              <strong>uma</strong> ordem quando a janela de consolidação fecha.
            </Estilo.Vazio>
          )}

          <Estilo.Lista>
            {ordens.map((linha) => (
              <Estilo.CartaoDaOrdem
                key={linha.ordemUuid}
                type="button"
                selecionado={linha.ordemUuid === selecionada}
                onClick={() => abrir(linha.ordemUuid)}
              >
                <Estilo.LinhaDoCartao>
                  <Estilo.Codigo>{linha.codigo}</Estilo.Codigo>
                  <Estilo.Selo tom={tomDoEstado(linha.estado)}>
                    {linha.estado.replace(/_/g, " ").toLowerCase()}
                  </Estilo.Selo>
                  <Estilo.Selo tom={linha.modoDeDisparo === "ENVIAR" ? "erro" : "info"}>
                    {linha.modoDeDisparo.toLowerCase()}
                  </Estilo.Selo>
                  {linha.envioSimuladoEm && !linha.enviadaEm && (
                    <Estilo.Selo tom="atencao">envio simulado — nada saiu</Estilo.Selo>
                  )}
                </Estilo.LinhaDoCartao>
                <Estilo.LinhaDoCartao>
                  <Estilo.Texto>
                    {linha.fornecedorNome} · {linha.lojaNome ?? linha.subsidiary} ·{" "}
                    {linha.linhas} linha(s) · {comoReais(linha.valorTotal)}
                  </Estilo.Texto>
                </Estilo.LinhaDoCartao>
                {(linha.faltaParaMinimo ||
                  linha.linhasAcimaDoTeto > 0 ||
                  linha.linhasSemPreco > 0) && (
                  <Estilo.LinhaDoCartao>
                    {linha.faltaParaMinimo ? (
                      <Estilo.Selo tom="atencao">
                        faltam {comoReais(linha.faltaParaMinimo)} para o pedido mínimo
                      </Estilo.Selo>
                    ) : null}
                    {linha.linhasAcimaDoTeto > 0 && (
                      <Estilo.Selo tom="atencao">
                        {linha.linhasAcimaDoTeto} linha(s) acima do teto de linha
                      </Estilo.Selo>
                    )}
                    {linha.linhasSemPreco > 0 && (
                      <Estilo.Selo tom="atencao">
                        {linha.linhasSemPreco} linha(s) sem preço
                      </Estilo.Selo>
                    )}
                  </Estilo.LinhaDoCartao>
                )}
              </Estilo.CartaoDaOrdem>
            ))}
          </Estilo.Lista>

          {ordem && detalhe && (
            <div style={{ marginTop: "var(--sp-20)" }}>
              <Estilo.Cabecalho>
                <div>
                  <Estilo.Titulo>{ordem.codigo}</Estilo.Titulo>
                  <Estilo.Subtitulo>
                    {ordem.fornecedorNome} · {ordem.lojaNome ?? ordem.subsidiary} ·
                    criada em {comoDataHora(ordem.createdAt)}
                  </Estilo.Subtitulo>
                </div>
                <div style={{ display: "flex", gap: "var(--sp-8)" }}>
                  <Estilo.Botao
                    type="button"
                    disabled={agindo || ordem.estado !== "AGUARDANDO_APROVACAO"}
                    onClick={() => decidir("aprovar", ordem.ordemUuid)}
                  >
                    Aprovar
                  </Estilo.Botao>
                  <Estilo.BotaoSecundario
                    type="button"
                    disabled={
                      agindo ||
                      !["AGUARDANDO_APROVACAO", "APROVADA"].includes(ordem.estado)
                    }
                    onClick={() => decidir("recusar", ordem.ordemUuid)}
                  >
                    Recusar
                  </Estilo.BotaoSecundario>
                  <Estilo.BotaoPerigo
                    type="button"
                    disabled={agindo || Boolean(ordem.enviadaEm)}
                    onClick={() => decidir("cancelar", ordem.ordemUuid)}
                  >
                    Cancelar
                  </Estilo.BotaoPerigo>
                </div>
              </Estilo.Cabecalho>

              {/* POR QUE ESTÁ EM SUGERIR — o critério do card. */}
              {ordem.modoDeDisparo === "SUGERIR" && motivos.length > 0 && (
                <Estilo.Painel tom="atencao">
                  <Estilo.TituloDoPainel>
                    Esta ordem precisa da sua aprovação. Por quê:
                  </Estilo.TituloDoPainel>
                  <Estilo.ListaDeMotivos>
                    {motivos.map((motivo) => (
                      <li key={motivo}>{motivo}</li>
                    ))}
                  </Estilo.ListaDeMotivos>
                  {detalhe.itens.some((i) => i.estouraTetoDeLinha) && (
                    <p style={{ marginBottom: 0 }}>
                      Linha(s) acima do teto:{" "}
                      {detalhe.itens
                        .filter((i) => i.estouraTetoDeLinha)
                        .map((i) => i.produto ?? i.skuUuid)
                        .join(", ")}
                    </p>
                  )}
                  {detalhe.itens.some((i) => i.semPreco) && (
                    <p style={{ marginBottom: 0 }}>
                      Linha(s) sem preço acordado vigente:{" "}
                      {detalhe.itens
                        .filter((i) => i.semPreco)
                        .map((i) => i.produto ?? i.skuUuid)
                        .join(", ")}
                    </p>
                  )}
                </Estilo.Painel>
              )}

              {ordem.faltaParaMinimo ? (
                <Estilo.Painel tom="atencao">
                  <Estilo.TituloDoPainel>
                    Abaixo do pedido mínimo do fornecedor
                  </Estilo.TituloDoPainel>
                  <Estilo.Texto>
                    Necessidade de {comoReais(ordem.valorTotal)} contra um pedido
                    mínimo de {comoReais(ordem.pedidoMinimoNoMomento)}:{" "}
                    <Estilo.Forte>
                      faltam {comoReais(ordem.faltaParaMinimo)}
                    </Estilo.Forte>
                    . A completação já trouxe os itens que estavam a até 30% acima do
                    ponto. Pagar frete, esperar ou comprar fora é decisão sua — nada
                    foi enviado.
                    {ordem.faltaParaFreteGratis ? (
                      <>
                        {" "}
                        Para o frete grátis faltam{" "}
                        {comoReais(ordem.faltaParaFreteGratis)}.
                      </>
                    ) : null}
                  </Estilo.Texto>
                </Estilo.Painel>
              ) : null}

              {ordem.motivoDeBloqueio && (
                <Estilo.Painel tom="erro">
                  <Estilo.TituloDoPainel>Ordem barrada na saída</Estilo.TituloDoPainel>
                  <Estilo.Texto>{ordem.motivoDeBloqueio}</Estilo.Texto>
                </Estilo.Painel>
              )}

              {ordem.liberadaEm && ordem.estado === "APROVADA" && (
                <Estilo.Bloco>
                  <Estilo.Icone>schedule</Estilo.Icone>
                  <Estilo.Texto>
                    Janela de arrependimento até{" "}
                    <Estilo.Forte>{comoDataHora(ordem.liberadaEm)}</Estilo.Forte>.
                    Cancelar antes disso não deixa vestígio no fornecedor, porque
                    nenhuma mensagem saiu.
                  </Estilo.Texto>
                </Estilo.Bloco>
              )}

              <Estilo.Tabela>
                <thead>
                  <tr>
                    <Estilo.Cabecalhos>Insumo</Estilo.Cabecalhos>
                    <Estilo.Cabecalhos $numerica>Quantidade</Estilo.Cabecalhos>
                    <Estilo.Cabecalhos>Unidade de compra</Estilo.Cabecalhos>
                    <Estilo.Cabecalhos $numerica>Preço acordado</Estilo.Cabecalhos>
                    <Estilo.Cabecalhos $numerica>Subtotal</Estilo.Cabecalhos>
                    <Estilo.Cabecalhos>Observação</Estilo.Cabecalhos>
                  </tr>
                </thead>
                <tbody>
                  {detalhe.itens.map((item) => (
                    <tr key={item.ordemItemUuid}>
                      <Estilo.Celula>{item.produto ?? item.skuUuid}</Estilo.Celula>
                      <Estilo.Celula $numerica>
                        {comoQuantidade(item.quantidade)}
                      </Estilo.Celula>
                      <Estilo.Celula>{item.unidadeCompra}</Estilo.Celula>
                      <Estilo.Celula $numerica $alerta={item.semPreco}>
                        {item.semPreco ? "sem preço vigente" : comoReais(item.precoUnitario)}
                      </Estilo.Celula>
                      <Estilo.Celula $numerica $alerta={item.estouraTetoDeLinha}>
                        {comoReais(item.subtotal)}
                      </Estilo.Celula>
                      <Estilo.Celula>
                        {item.origem === "completacao" && (
                          <Estilo.Selo tom="info">completação</Estilo.Selo>
                        )}{" "}
                        {item.estouraTetoDeLinha && (
                          <Estilo.Selo tom="atencao">
                            acima do teto de {comoReais(item.tetoDeLinhaNoMomento)}
                          </Estilo.Selo>
                        )}
                      </Estilo.Celula>
                    </tr>
                  ))}
                  <Estilo.Rodape>
                    <td colSpan={4}>
                      Total da ordem ({ordem.quantidadeDeItens} itens)
                    </td>
                    <td style={{ textAlign: "right" }}>
                      {comoReais(ordem.valorTotal)}
                    </td>
                    <td />
                  </Estilo.Rodape>
                </tbody>
              </Estilo.Tabela>

              {detalhe.mensagens.length > 0 && (
                <div style={{ marginTop: "var(--sp-20)" }}>
                  <Estilo.TituloDoPainel>
                    Mensagens desta ordem
                  </Estilo.TituloDoPainel>
                  {detalhe.mensagens.map((mensagem) => (
                    <Estilo.Bloco
                      key={mensagem.mensagemUuid}
                      style={{ flexDirection: "column", alignItems: "flex-start" }}
                    >
                      <Estilo.LinhaDoCartao>
                        <Estilo.Selo
                          tom={mensagem.estado === "enviada" ? "erro" : "info"}
                        >
                          {mensagem.estado}
                        </Estilo.Selo>
                        <Estilo.Texto>
                          {mensagem.canal} · {mensagem.destinatario} ·{" "}
                          {comoDataHora(mensagem.criadaEm)}
                        </Estilo.Texto>
                      </Estilo.LinhaDoCartao>
                      <Estilo.Texto>
                        {mensagem.estado === "simulada"
                          ? "Este é o texto exato que sairia. Nada saiu para o fornecedor."
                          : "Texto exato registrado."}
                      </Estilo.Texto>
                      <Estilo.TextoExato>{mensagem.textoExato}</Estilo.TextoExato>
                    </Estilo.Bloco>
                  ))}
                </div>
              )}

              {omitido.length > 0 && (
                <Estilo.Vazio>
                  Alguns números não vieram do servidor porque sua permissão não os
                  alcança: {omitido.join(", ")}. Eles aparecem como “—”, e não como
                  zero.
                </Estilo.Vazio>
              )}
            </div>
          )}
        </AlfredoStyles.FlexItem>
      </AlfredoStyles.FlexContainer>
    </AlfredoStyles.Container>
  );
};

export default OrdersPage;
